import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Box,
  Heading,
  TextField,
  Button,
  Text,
  DialogTrigger,
  LoadingOrbit,
  Plus,
  FolderOpen,
  EmptyState,
  Page,
  PageHeader,
  Tooltip,
  TooltipTrigger,
  SteppedDialog,
  PillToggle,
  type StepContent,
} from '@chatsdet/ui';
import { API_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { PropertyCard } from '../../features/properties/components/PropertyCard';
import type { PropertyCardItem } from '../../features/properties/components/PropertyCard';
import {
  DefaultEnvironmentStep,
  type DefaultEnvironmentValue,
} from '../../features/properties/components/DefaultEnvironmentStep';

type PropertyItem = PropertyCardItem & {
  updatedAt?: string;
};

type PropertyQuotaStatus = {
  maxProperties: number;
  currentProperties: number;
  canCreate: boolean;
  isSuperadmin: boolean;
};

function usePropertyQuotaQuery() {
  return useQuery({
    queryKey: ['property-quota'],
    queryFn: async (): Promise<{ ok: boolean; quota: PropertyQuotaStatus }> => {
      const r = await fetch(API_URL + '/org/properties/quota', { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch quota');
      return r.json();
    },
  });
}

type PropertiesViewProps = {
  layout?: 'page' | 'embedded';
  showBreadcrumbs?: boolean;
};

export function PropertiesView({ layout = 'page', showBreadcrumbs = layout === 'page' }: PropertiesViewProps = {}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const mcpTestingEnabled = !!user?.features?.mcpTesting;
  const [form, setForm] = React.useState({ name: '', description: '' });
  const [appType, setAppType] = React.useState<'web' | 'mcp'>('web');
  const [env, setEnv] = React.useState<DefaultEnvironmentValue>({
    name: 'staging',
    url: '',
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const quotaQuery = usePropertyQuotaQuery();

  const handleViewProperty = (id: string) => {
    navigate({ to: '/test-data/applications/$propertyId/settings', params: { propertyId: id } });
  };

  const breadcrumbs = showBreadcrumbs
    ? [
        {
          label: 'Home',
          onPress: () => navigate({ to: '/' }),
        },
        {
          label: 'Applications',
          onPress: () => navigate({ to: '/' }),
        },
      ]
    : undefined;
  useBreadcrumbs(breadcrumbs);

  const propsQ = useQuery({
    queryKey: ['properties'],
    queryFn: async (): Promise<{ items: PropertyItem[] }> => {
      const r = await fetch(API_URL + '/org/properties', { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch properties');
      return r.json();
    },
  });

  const normalizeEnvUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return trimmed;
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const createProp = useMutation({
    mutationFn: async () => {
      const url = normalizeEnvUrl(env.url);
      const r = await fetch(API_URL + '/org/properties', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          description: form.description || undefined,
          ...(appType === 'mcp' ? { type: 'mcp' as const } : {}),
          defaultEnvironment: { name: env.name, url },
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => {
      setForm({ name: '', description: '' });
      setAppType('web');
      setEnv({ name: 'staging', url: '' });
      setIsCreateModalOpen(false);
      qc.invalidateQueries({ queryKey: ['properties'] });
      qc.invalidateQueries({ queryKey: ['environment-kinds'] });
    },
  });

  const canCreateProperty = quotaQuery.data?.quota?.canCreate ?? true;
  const quotaStatus = quotaQuery.data?.quota;

  const steps: StepContent[] = [
    {
      id: 'details',
      label: 'Application details',
      render: () => (
        <div className="space-y-4">
          <div>
            <Heading level={3} className="text-base font-semibold">
              Name this application
            </Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              An application is one app or site you want to test. URL comes next.
            </Text>
          </div>
          <TextField
            label="Application Name"
            value={form.name}
            onChange={(v) => setForm((f) => ({ ...f, name: v }))}
            placeholder="e.g., Acme Marketing Site"
            isRequired
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            placeholder="Optional — what does this application do?"
          />
          {mcpTestingEnabled && (
            <Box>
              <Text size="sm" weight="medium" className="mb-2">
                Application type
              </Text>
              <PillToggle
                aria-label="Application type"
                value={appType}
                onChange={setAppType}
                options={[
                  { value: 'web', label: 'Web app' },
                  { value: 'mcp', label: 'MCP server' },
                ]}
              />
            </Box>
          )}
        </div>
      ),
      validate: () => form.name.trim().length > 0,
    },
    {
      id: 'default-env',
      label: 'Default environment',
      render: () => (
        <div className="space-y-4">
          <div>
            <Heading level={3} className="text-base font-semibold">
              Default environment
            </Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              {appType === 'mcp'
                ? 'One deployment of this MCP server (staging, prod, etc). The URL is the MCP endpoint.'
                : 'One deployment of this application (staging, prod, etc). Add more later.'}
            </Text>
          </div>
          <DefaultEnvironmentStep value={env} onChange={setEnv} isMcp={appType === 'mcp'} />
        </div>
      ),
      validate: () => {
        if (!env.name.trim()) return false;
        if (!env.url.trim()) return false;
        try {
          new URL(normalizeEnvUrl(env.url));
          return true;
        } catch {
          return false;
        }
      },
    },
  ];

  const addPropertyButton = canCreateProperty ? (
    <DialogTrigger
      isOpen={isCreateModalOpen}
      onOpenChange={(open) => {
        setIsCreateModalOpen(open);
        if (!open) {
          setForm({ name: '', description: '' });
          setAppType('web');
          setEnv({ name: 'staging', url: '' });
        }
      }}
    >
      <Button variant="primary" iconLeft={<Plus size={16} />}>
        Add Application
      </Button>
      <SteppedDialog
        title="Create New Application"
        steps={steps}
        finishButtonText={createProp.isPending ? 'Creating...' : 'Create Application'}
        isNavigationDisabled={createProp.isPending}
        onCancel={() => setIsCreateModalOpen(false)}
        onFinish={() => createProp.mutateAsync()}
      />
    </DialogTrigger>
  ) : (
    <TooltipTrigger>
      <Button variant="primary" iconLeft={<Plus size={16} />} isDisabled>
        Add Application
      </Button>
      <Tooltip>
        Application limit reached ({quotaStatus?.currentProperties ?? 0}/{quotaStatus?.maxProperties ?? 1}). Contact support to increase.
      </Tooltip>
    </TooltipTrigger>
  );

  const headerActions = addPropertyButton;

  const listContent = (
    <div>
      {propsQ.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : propsQ.data?.items && propsQ.data.items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {propsQ.data.items.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              onView={handleViewProperty}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<FolderOpen size={24} />}
          title="No applications yet"
          description="Create your first application to get started"
        />
      )}
    </div>
  );

  if (layout === 'page') {
    return (
      <Page>
        <PageHeader
          icon={<FolderOpen size={24} />}
          title="Applications"
          description="Manage your applications and their configurations"
          actions={headerActions}
        />
        {listContent}
      </Page>
    );
  }

  const propertyCount = propsQ.data?.items?.length ?? 0;

  return (
    <Box className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Heading level={2}>Applications</Heading>
          <Text size="sm" className="text-muted-foreground">
            {propertyCount} {propertyCount === 1 ? 'application' : 'applications'} configured for testing
          </Text>
        </div>
        {headerActions}
      </div>
      {listContent}
    </Box>
  );
}

export function Properties() {
  return <PropertiesView />;
}
